import React from 'react';
import noop from 'lodash/noop';
import { unreachable } from 'js/sign-components/common/ts-utils';
import { useFeatureFlag } from 'js/sign-components/common/feature-flag';
import { useContextCacher } from 'signer-app/utils/legacy-context-utilities';
import { Field } from 'signer-app/types/editor-types';
import { PREPARER } from 'signer-app/signature-request/constants';
import Signature from 'signer-app/signature-request/display-field/signature';
import Checkbox from 'signer-app/signature-request/display-field/checkbox';
import Dropdown, {
  Props as DropdownProps,
} from 'signer-app/signature-request/display-field/dropdown';
import Radio, {
  Props as RadioProps,
} from 'signer-app/signature-request/display-field/radio';
import DateField from 'signer-app/signature-request/display-field/date';
import Text from 'signer-app/signature-request/display-field/text';
import Hyperlink from 'signer-app/signature-request/display-field/hyperlink';
import Rectangle from 'signer-app/signature-request/display-field/rectangle';
import Image from 'signer-app/signature-request/display-field/image';
import { useSignatureRequestContext } from 'signer-app/signature-request/context';
import { InitialsField } from './signature-components/initials-field';
import { SignatureField } from './signature-components/signature-field';
import { ImperativeAPI } from './types/imperative-api';
import { ImageField } from './image-components/image-field';

export type WrapperStyles = React.CSSProperties & {
  height: string;
  width: string;
};

type FieldContext = {
  fieldData: Field;
  wrapperStyles: WrapperStyles;
  height: string;
  width: string;
  isFormView?: boolean;
};

type ProviderProps = React.PropsWithChildren<{
  fieldData: Field;
  wrapperStyles: WrapperStyles;
  isFormView?: boolean;
}>;

export const fieldContext = React.createContext<FieldContext>(
  {} as FieldContext,
);

export function FieldContextProvider({
  fieldData,
  wrapperStyles,
  isFormView,
  children,
}: ProviderProps) {
  const contextCache = useContextCacher<FieldContext>();
  return (
    <fieldContext.Provider
      value={contextCache({
        fieldData,
        height: wrapperStyles.height,
        width: wrapperStyles.width,
        wrapperStyles,
        isFormView,
      })}
    >
      {children}
    </fieldContext.Provider>
  );
}

// This is a SUPER awkward API and I wish I hadn't written it this way.
// Depending on which type of Field you have, props needs to be the matching
// props for  its implementation from the `componentMap`.
export type Props =
  | {
      fieldData: Field;
      style?: React.CSSProperties;
      placeholder?: string;
      onKeDown?: undefined | ((event: React.KeyboardEvent) => void);
      onChange: (e: React.ChangeEvent) => void;
    }
  | Partial<RadioProps>
  | Partial<DropdownProps>;

// This whole component's concept was a mistake. It got created while attempting
// to separate code that was formerly just Editor into code to be shared across
// Editor, Signer, and Overlay. Before TypeScript this used a `componentMap`
// object where the keys were field type and the values were the specific
// component implementation. I fought with trying to get TypeScript to understand
// this and eventually had to give up. Now that object is gone, and we just have
// a huge switch statement where every component gets the same props.
function DisplayField(
  props: Props & { isFormView?: boolean },
  ref: React.Ref<ImperativeAPI>,
) {
  const advancedSigFrameFF = useFeatureFlag(
    'sign_core_2026_01_07_advanced_sig_frame',
  );
  const { fieldData, height, width, wrapperStyles, isFormView } =
    React.useContext(fieldContext);
  const { isOverlay, signatureDateTimestamp } = useSignatureRequestContext();
  const readOnly = fieldData.readOnly || false;

  // The caller can override the field data. The Editor may use this to show a
  // draft version of the field, or to replace the `.value` with the `.name`
  // for display purposes.
  const fd = React.useMemo(() => {
    const fieldDataToUse = props.fieldData || fieldData;
    return {
      ...fieldDataToUse,
      required: fieldDataToUse.readOnly ? false : fieldDataToUse.required,
    };
  }, [props.fieldData, fieldData]);

  let propOptions: any = {
    'data-qa-ref': fd.type === 'checkbox' ? fd.type : `${fd.type}-input`,
    ...props,
  };

  // For readOnly Fields we disabled all actions
  if (readOnly) {
    propOptions = {
      ...propOptions,
      disabled: true,
      onKeyPress: noop,
      onTouchEnd: noop,
      onClick: noop,
      onChange: noop,
      tabIndex: -1,
    };
  }

  let fieldImpl;
  switch (fd.type) {
    case 'signature':
      fieldImpl = advancedSigFrameFF ? (
        <SignatureField
          fieldData={fd}
          isFormView={isFormView}
          {...propOptions}
          showTimestamp={signatureDateTimestamp}
          width={width}
          height={height}
          ref={ref}
        />
      ) : (
        <Signature
          fieldData={fd}
          isFormView={isFormView}
          {...propOptions}
          showTimestamp={signatureDateTimestamp}
          width={width}
          height={height}
          ref={ref}
        />
      );
      break;
    case 'initials':
      fieldImpl = advancedSigFrameFF ? (
        <InitialsField
          fieldData={fd}
          isFormView={isFormView}
          {...propOptions}
          width={width}
          height={height}
          ref={ref}
        />
      ) : (
        <Signature
          fieldData={fd}
          isFormView={isFormView}
          {...propOptions}
          width={width}
          height={height}
          ref={ref}
        />
      );
      break;
    case 'text':
      fieldImpl = (
        <Text
          fieldData={fd}
          {...propOptions}
          isOverlay={isOverlay}
          width={width}
          height={height}
          ref={ref}
        />
      );
      break;
    case 'date':
      if (fd.signer === PREPARER) {
        fieldImpl = (
          <Text
            fieldData={fd}
            isOverlay={isOverlay}
            {...propOptions}
            width={width}
            height={height}
            ref={ref}
          />
        );
      } else {
        fieldImpl = (
          <DateField
            fieldData={fd}
            {...propOptions}
            width={width}
            height={height}
            ref={ref}
          />
        );
      }
      break;
    case 'checkbox':
      fieldImpl = (
        <Checkbox
          fieldData={fd}
          {...propOptions}
          width={width}
          height={height}
          ref={ref}
        />
      );
      break;
    case 'dropdown':
      fieldImpl = (
        <Dropdown
          fieldData={fd}
          {...propOptions}
          width={width}
          height={height}
          ref={ref}
        />
      );
      break;
    case 'radiobutton':
      fieldImpl = (
        <Radio
          fieldData={fd}
          {...propOptions}
          width={width}
          height={height}
          ref={ref}
        />
      );
      break;
    case 'hyperlink':
      fieldImpl = (
        <Hyperlink
          fieldData={fd}
          {...propOptions}
          width={width}
          height={height}
          ref={ref}
        />
      );
      break;
    case 'rectangle':
      fieldImpl = <Rectangle />;
      break;
    case 'image':
      fieldImpl = advancedSigFrameFF ? (
        <ImageField id={fd.src} ref={ref} />
      ) : (
        <Image id={fd.src} ref={ref} />
      );
      break;
    default:
      unreachable(fd);
  }

  return <div style={wrapperStyles}>{fieldImpl}</div>;
}

export default React.memo(React.forwardRef(DisplayField));
